// Colaboradores Management Component
import { createElement } from '@/utils/dom'
import { colaboradoresApi, type Colaborador, type ColaboradorCountInfo, type CreateColaboradorData, type UpdateColaboradorData } from '@/services/colaboradores'
import { townsApi, type Town } from '@/services/towns'
import { router } from '@/utils/router'
import { authService } from '@/services/auth'

interface ColaboradoresComponentOptions {
  onColaboradorChange?: () => void
}

export class ColaboradoresComponent {
  private colaboradores: Colaborador[] = []
  private countInfo: ColaboradorCountInfo = { count: 0, max_colaboradores: 1, can_add_more: true }
  private container: HTMLElement | null = null
  private onColaboradorChange?: () => void
  private towns: Town[] = []
  private isCollapsed: boolean = false
  private static readonly STORAGE_KEY = 'colaboradores_collapsed'

  constructor(options?: ColaboradoresComponentOptions) {
    this.onColaboradorChange = options?.onColaboradorChange
    // Load collapsed state from localStorage
    this.isCollapsed = localStorage.getItem(ColaboradoresComponent.STORAGE_KEY) === 'true'
  }

  async render(): Promise<HTMLElement> {
    this.container = createElement('div', { className: 'colaboradores-section' })

    // Initial render with loading state
    this.container.innerHTML = this.createLoadingState()

    // Load data
    await this.loadColaboradores()

    return this.container
  }

  private createLoadingState(): string {
    return `
      <div class="section-header">
        <h2><i class="fa-solid fa-users"></i> Colaboradores</h2>
      </div>
      <div class="loading-state" style="text-align: center; padding: 2rem;">
        <div class="loading"></div>
        <p style="margin-top: 1rem; color: var(--text-secondary);">A carregar colaboradores...</p>
      </div>
    `
  }

  private async loadColaboradores(): Promise<void> {
    try {
      const response = await colaboradoresApi.list()
      this.colaboradores = response.colaboradores
      this.countInfo = response.count_info
      this.updateUI()
    } catch (error) {
      console.error('Failed to load colaboradores:', error)
      this.colaboradores = []
      this.countInfo = { count: 0, max_colaboradores: 1, can_add_more: true }
      this.updateUI()
    }
  }

  private updateUI(): void {
    if (!this.container) return

    this.container.innerHTML = this.createContent()
    this.addEventListeners()
  }

  private createContent(): string {
    const collapseClass = this.isCollapsed ? 'collapsed' : 'expanded'
    const arrowIcon = this.isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'

    return `
      <div class="section-header colaboradores-header colaboradores-toggle-header" id="colaboradores-toggle">
        <div class="header-left">
          <h2><i class="fa-solid fa-users"></i> Colaboradores</h2>
          <span class="count-badge ${this.countInfo.can_add_more ? '' : 'limit-reached'}">
            ${this.countInfo.count}/${this.countInfo.max_colaboradores === 9999 ? '∞' : this.countInfo.max_colaboradores}
          </span>
        </div>
        <div class="header-right">
          <button class="btn btn-primary btn-sm" id="add-colaborador-btn">
            <i class="fa-solid fa-plus"></i> Adicionar Colaborador
          </button>
          <button class="btn btn-icon btn-sm colaboradores-collapse-btn" id="colaboradores-collapse-btn" title="${this.isCollapsed ? 'Expandir' : 'Minimizar'}">
            <i class="fa-solid ${arrowIcon}"></i>
          </button>
        </div>
      </div>

      <div class="colaboradores-content-wrapper ${collapseClass}" id="colaboradores-content">
        ${this.colaboradores.length === 0 ? this.createEmptyState() : this.createColaboradoresList()}
      </div>
    `
  }

  private createEmptyState(): string {
    return `
      <div class="empty-state colaboradores-empty">
        <div class="empty-icon">
          <i class="fa-solid fa-users-slash"></i>
        </div>
        <h3>Nenhum colaborador registado</h3>
        ${this.countInfo.can_add_more ? `
          <button class="btn btn-primary" id="add-first-colaborador-btn">
            <i class="fa-solid fa-plus"></i> Adicionar Primeiro Colaborador
          </button>
        ` : ''}
      </div>
    `
  }

  private createColaboradoresList(): string {
    return `
      <div class="colaboradores-table">
        <div class="table-header colaboradores-table-header">
          <div>Funcionário</div>
          <div>Empresa</div>
          <div>Veículo</div>
          <div>Matrícula</div>
          <div>Cidade</div>
          <div>Ações</div>
        </div>
        ${this.colaboradores.map(colaborador => this.createColaboradorRow(colaborador)).join('')}
      </div>
    `
  }

  private createColaboradorRow(colaborador: Colaborador): string {
    const vehicleTypeMap: Record<string, string> = {
      'CAR': '🚗 Automóvel',
      'BIKE': '🏍️ Motociclo',
      'VAN': '🚐 Carrinha',
      'TRUCK': '🚚 Camião'
    }

    const vehicleDisplay = vehicleTypeMap[colaborador.tipo_veiculo] || colaborador.tipo_veiculo

    return `
      <div class="table-row colaborador-row" data-id="${colaborador.id}">
        <div class="table-cell" data-label="Funcionário">
          <strong>${this.escapeHtml(colaborador.funcionario)}</strong>
          <span class="nif-badge">${colaborador.nif}</span>
        </div>
        <div class="table-cell" data-label="Empresa">${this.escapeHtml(colaborador.empresa)}</div>
        <div class="table-cell" data-label="Veículo">
          ${vehicleDisplay} (${colaborador.ano_veiculo})
        </div>
        <div class="table-cell" data-label="Matrícula">
          <code class="matricula">${this.escapeHtml(colaborador.matricula)}</code>
        </div>
        <div class="table-cell" data-label="Cidade">${this.escapeHtml(colaborador.cidade)}</div>
        <div class="table-cell table-actions" data-label="Ações">
          <button class="btn btn-icon btn-sm edit-colaborador-btn" data-id="${colaborador.id}" title="Editar">
            <i class="fa-solid fa-pen"></i>
          </button>
          <button class="btn btn-icon btn-sm btn-danger delete-colaborador-btn" data-id="${colaborador.id}" title="Eliminar">
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    `
  }

  private addEventListeners(): void {
    if (!this.container) return

    // Collapse/Expand toggle - clicking anywhere on the header (except action buttons)
    const header = this.container.querySelector('#colaboradores-toggle')
    header?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      // Don't toggle if clicking on the "Add Colaborador" button
      if (target.closest('#add-colaborador-btn')) {
        return
      }
      this.toggleCollapse()
    })

    // Add colaborador buttons
    const addBtn = this.container.querySelector('#add-colaborador-btn')
    const addFirstBtn = this.container.querySelector('#add-first-colaborador-btn')

    addBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.showAddModal()
    })
    addFirstBtn?.addEventListener('click', () => this.showAddModal())

    // Upgrade button
    const upgradeBtn = this.container.querySelector('#upgrade-plan-btn')
    upgradeBtn?.addEventListener('click', () => router.navigate('/pricing'))

    // Edit buttons
    this.container.querySelectorAll('.edit-colaborador-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const id = (btn as HTMLElement).dataset.id
        if (id) this.showEditModal(id)
      })
    })

    // Delete buttons
    this.container.querySelectorAll('.delete-colaborador-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const id = (btn as HTMLElement).dataset.id
        if (id) this.showDeleteConfirmation(id)
      })
    })
  }

  private toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed

    // Persist to localStorage
    localStorage.setItem(ColaboradoresComponent.STORAGE_KEY, String(this.isCollapsed))

    // Update the UI
    if (!this.container) return

    const contentWrapper = this.container.querySelector('#colaboradores-content')
    const collapseBtn = this.container.querySelector('#colaboradores-collapse-btn')

    if (contentWrapper) {
      contentWrapper.classList.toggle('collapsed', this.isCollapsed)
      contentWrapper.classList.toggle('expanded', !this.isCollapsed)
    }

    if (collapseBtn) {
      const icon = collapseBtn.querySelector('i')
      if (icon) {
        icon.className = `fa-solid ${this.isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`
      }
      collapseBtn.setAttribute('title', this.isCollapsed ? 'Expandir' : 'Minimizar')
    }
  }

  private async showAddModal(): Promise<void> {
    // Check if user can add more collaborators
    if (!this.countInfo.can_add_more) {
      this.showUpgradePromptModal()
      return
    }
    await this.showColaboradorModal(null)
  }

  private showUpgradePromptModal(): void {
    const user = authService.getUser()
    const planName = user?.plan_id || 'free'

    // Determine what upgrade message to show based on current plan
    let upgradeMessage = ''
    let upgradeAction = ''

    if (planName === 'solo' || this.countInfo.max_colaboradores <= 6) {
      upgradeMessage = 'Limite de colaboradores atingido! Faça upgrade para o plano Empresas para adicionar até 60 colaboradores.'
      upgradeAction = 'upgrade'
    } else if (planName === 'empresas' || this.countInfo.max_colaboradores <= 60) {
      upgradeMessage = 'Limite de colaboradores atingido! Contacte-nos para o plano Ultra Premium com colaboradores ilimitados.'
      upgradeAction = 'contact'
    }

    // Remove existing modal
    const existingModal = document.querySelector('.modal-overlay.upgrade-limit-modal')
    existingModal?.remove()

    const modal = createElement('div', { className: 'modal-overlay upgrade-limit-modal' })
    modal.innerHTML = `
      <div class="modal-content upgrade-modal-content">
        <div class="modal-header">
          <h2>🚀 Limite Atingido</h2>
          <button class="modal-close" id="close-upgrade-modal">×</button>
        </div>
        
        <div class="upgrade-modal-body">
          <div class="upgrade-icon-large">
            <i class="fa-solid fa-users"></i>
          </div>
          <p class="upgrade-message">${upgradeMessage}</p>
          <p class="upgrade-current">Atualmente tem <strong>${this.countInfo.count}/${this.countInfo.max_colaboradores}</strong> colaboradores.</p>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn btn-outline" id="cancel-upgrade-modal">
            Fechar
          </button>
          ${upgradeAction === 'upgrade' ? `
            <button type="button" class="btn btn-primary" id="go-to-pricing-btn">
              Ver Planos <i class="fa-solid fa-arrow-right"></i>
            </button>
          ` : `
            <a href="mailto:contacto@mapaskm.pt" class="btn btn-primary">
              Contactar <i class="fa-solid fa-envelope"></i>
            </a>
          `}
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Event handlers
    const closeBtn = modal.querySelector('#close-upgrade-modal')
    const cancelBtn = modal.querySelector('#cancel-upgrade-modal')
    const pricingBtn = modal.querySelector('#go-to-pricing-btn')

    const closeModal = () => modal.remove()

    closeBtn?.addEventListener('click', closeModal)
    cancelBtn?.addEventListener('click', closeModal)
    pricingBtn?.addEventListener('click', () => {
      closeModal()
      router.navigate('/pricing')
    })

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal()
    })

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)
  }

  private async showEditModal(id: string): Promise<void> {
    const colaborador = this.colaboradores.find(c => c.id === id)
    if (colaborador) {
      await this.showColaboradorModal(colaborador)
    }
  }

  private async showColaboradorModal(colaborador: Colaborador | null): Promise<void> {
    // Remove existing modal
    const existingModal = document.querySelector('.modal-overlay.colaborador-modal')
    existingModal?.remove()

    const isEdit = colaborador !== null
    const title = isEdit ? 'Editar Colaborador' : 'Adicionar Colaborador'

    // Get user data for defaults
    const user = authService.getUser()
    const defaultEmpresa = colaborador?.empresa || user?.company_name || ''
    const defaultNif = colaborador?.nif || user?.nif || ''

    // Load towns for dropdown if not already loaded
    if (this.towns.length === 0) {
      try {
        this.towns = await townsApi.getAll()
      } catch (error) {
        console.error('Failed to load towns:', error)
        this.towns = []
      }
    }

    const modal = createElement('div', { className: 'modal-overlay colaborador-modal' })
    modal.innerHTML = `
      <div class="modal-content colaborador-modal-content">
        <div class="modal-header">
          <h2>${title}</h2>
          <button class="modal-close" id="close-colaborador-modal">×</button>
        </div>
        
        <form id="colaborador-form" class="colaborador-form">
          <div class="form-row">
            <div class="form-group">
              <label for="colab-empresa" class="form-label">
                Empresa <span class="required">*</span>
              </label>
              <input 
                type="text" 
                id="colab-empresa" 
                name="empresa" 
                class="form-input" 
                value="${this.escapeHtml(defaultEmpresa)}"
                required
                placeholder="Nome da empresa"
              />
            </div>
            
            <div class="form-group">
              <label for="colab-nif" class="form-label">
                NIF <span class="required">*</span>
              </label>
              <input 
                type="text" 
                id="colab-nif" 
                name="nif" 
                class="form-input" 
                value="${colaborador?.nif || defaultNif}"
                required
                maxlength="9"
                pattern="[0-9]{9}"
                placeholder="123456789"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="colab-funcionario" class="form-label">
              Nome do Funcionário <span class="required">*</span>
            </label>
            <input 
              type="text" 
              id="colab-funcionario" 
              name="funcionario" 
              class="form-input" 
              value="${this.escapeHtml(colaborador?.funcionario || '')}"
              required
              placeholder="Nome completo do funcionário"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="colab-tipo-veiculo" class="form-label">
                Tipo de Veículo <span class="required">*</span>
              </label>
              <select 
                id="colab-tipo-veiculo" 
                name="tipo_veiculo" 
                class="form-input" 
                required
              >
                <option value="">Selecionar...</option>
                <option value="CAR" ${colaborador?.tipo_veiculo === 'CAR' ? 'selected' : ''}>🚗 Automóvel</option>
                <option value="BIKE" ${colaborador?.tipo_veiculo === 'BIKE' ? 'selected' : ''}>🏍️ Motociclo</option>
                <option value="VAN" ${colaborador?.tipo_veiculo === 'VAN' ? 'selected' : ''}>🚐 Carrinha</option>
                <option value="TRUCK" ${colaborador?.tipo_veiculo === 'TRUCK' ? 'selected' : ''}>🚚 Camião</option>
              </select>
            </div>

            <div class="form-group">
              <label for="colab-ano-veiculo" class="form-label">
                Ano do Veículo <span class="required">*</span>
              </label>
              <input 
                type="number" 
                id="colab-ano-veiculo" 
                name="ano_veiculo" 
                class="form-input" 
                value="${colaborador?.ano_veiculo || new Date().getFullYear()}"
                required
                min="1990"
                max="${new Date().getFullYear() + 1}"
                placeholder="${new Date().getFullYear()}"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="colab-matricula" class="form-label">
                Matrícula <span class="required">*</span>
              </label>
              <input 
                type="text" 
                id="colab-matricula" 
                name="matricula" 
                class="form-input" 
                value="${this.escapeHtml(colaborador?.matricula || '')}"
                required
                placeholder="AA-00-BB"
                style="text-transform: uppercase;"
              />
            </div>

            <div class="form-group">
              <label for="colab-cidade" class="form-label">
                Cidade <span class="required">*</span>
              </label>
              <select 
                id="colab-cidade" 
                name="cidade" 
                class="form-input" 
                required
              >
                <option value="">Selecionar cidade...</option>
                ${this.towns.map(town => `
                  <option value="${this.escapeHtml(town.name)}" ${colaborador?.cidade === town.name ? 'selected' : ''}>
                    ${this.escapeHtml(town.name)}
                  </option>
                `).join('')}
              </select>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-outline" id="cancel-colaborador-btn">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" id="save-colaborador-btn">
              <span class="btn-text">${isEdit ? 'Guardar Alterações' : 'Adicionar Colaborador'}</span>
              <span class="btn-loading hidden">
                <div class="loading-small"></div>
                A processar...
              </span>
            </button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(modal)

    // Focus on first unfilled input
    const firstInput = modal.querySelector('#colab-funcionario') as HTMLInputElement
    setTimeout(() => firstInput?.focus(), 100)

    // Add modal event handlers
    this.addModalEventHandlers(modal, colaborador)
  }

  private addModalEventHandlers(modal: HTMLElement, colaborador: Colaborador | null): void {
    const form = modal.querySelector('#colaborador-form') as HTMLFormElement
    const closeBtn = modal.querySelector('#close-colaborador-modal')
    const cancelBtn = modal.querySelector('#cancel-colaborador-btn')
    const saveBtn = modal.querySelector('#save-colaborador-btn') as HTMLButtonElement

    let isClosing = false

    const closeModal = () => {
      if (isClosing) return
      isClosing = true
      modal.remove()
    }

    closeBtn?.addEventListener('click', closeModal)
    cancelBtn?.addEventListener('click', closeModal)

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal()
    })

    // Close on escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      if (isClosing || saveBtn.disabled) return

      const formData = new FormData(form)
      const data: CreateColaboradorData = {
        empresa: formData.get('empresa') as string,
        nif: formData.get('nif') as string,
        funcionario: formData.get('funcionario') as string,
        tipo_veiculo: formData.get('tipo_veiculo') as string,
        ano_veiculo: parseInt(formData.get('ano_veiculo') as string),
        matricula: (formData.get('matricula') as string).toUpperCase(),
        cidade: formData.get('cidade') as string
      }

      // Validate
      if (!this.validateColaboradorForm(data)) return

      // Show loading
      const btnText = saveBtn.querySelector('.btn-text') as HTMLElement
      const btnLoading = saveBtn.querySelector('.btn-loading') as HTMLElement
      btnText?.classList.add('hidden')
      btnLoading?.classList.remove('hidden')
      saveBtn.disabled = true

      try {
        if (colaborador) {
          // Update
          await colaboradoresApi.update(colaborador.id, data as UpdateColaboradorData)
          this.showToast('Colaborador atualizado com sucesso!', 'success')
        } else {
          // Create
          await colaboradoresApi.create(data)
          this.showToast('Colaborador adicionado com sucesso!', 'success')
        }

        closeModal()
        await this.loadColaboradores()
        this.onColaboradorChange?.()
      } catch (error: any) {
        console.error('Failed to save colaborador:', error)
        let errorMessage = 'Erro ao guardar colaborador. Tente novamente.'

        if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        this.showToast(errorMessage, 'error')

        // Reset button
        btnText?.classList.remove('hidden')
        btnLoading?.classList.add('hidden')
        saveBtn.disabled = false
      }
    })
  }

  private validateColaboradorForm(data: CreateColaboradorData): boolean {
    if (!data.empresa.trim()) {
      this.showToast('Empresa é obrigatória', 'error')
      return false
    }

    if (!data.nif || data.nif.length !== 9 || !/^\d{9}$/.test(data.nif)) {
      this.showToast('NIF deve ter 9 dígitos', 'error')
      return false
    }

    if (!data.funcionario.trim()) {
      this.showToast('Nome do funcionário é obrigatório', 'error')
      return false
    }

    if (!data.tipo_veiculo) {
      this.showToast('Tipo de veículo é obrigatório', 'error')
      return false
    }

    const currentYear = new Date().getFullYear()
    if (!data.ano_veiculo || data.ano_veiculo < 1990 || data.ano_veiculo > currentYear + 1) {
      this.showToast(`Ano do veículo deve estar entre 1990 e ${currentYear + 1}`, 'error')
      return false
    }

    if (!data.matricula.trim()) {
      this.showToast('Matrícula é obrigatória', 'error')
      return false
    }

    if (!data.cidade.trim()) {
      this.showToast('Cidade é obrigatória', 'error')
      return false
    }

    return true
  }

  private showDeleteConfirmation(id: string): void {
    const colaborador = this.colaboradores.find(c => c.id === id)
    if (!colaborador) return

    // Remove existing modal
    const existingModal = document.querySelector('.modal-overlay.delete-modal')
    existingModal?.remove()

    const modal = createElement('div', { className: 'modal-overlay delete-modal' })
    modal.innerHTML = `
      <div class="modal-content delete-modal-content">
        <div class="modal-header">
          <h2>Eliminar Colaborador</h2>
          <button class="modal-close" id="close-delete-modal">×</button>
        </div>
        
        <div class="delete-confirmation">
          <div class="delete-icon">
            <i class="fa-solid fa-triangle-exclamation"></i>
          </div>
          <h3>Tem a certeza?</h3>
          <p>Está prestes a eliminar o colaborador <strong>${this.escapeHtml(colaborador.funcionario)}</strong>.</p>
          <p class="delete-warning">Esta ação não pode ser desfeita.</p>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn btn-outline" id="cancel-delete-btn">
            Cancelar
          </button>
          <button type="button" class="btn btn-danger" id="confirm-delete-btn">
            <span class="btn-text">Eliminar</span>
            <span class="btn-loading hidden">
              <div class="loading-small"></div>
            </span>
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Event handlers
    const closeBtn = modal.querySelector('#close-delete-modal')
    const cancelBtn = modal.querySelector('#cancel-delete-btn')
    const confirmBtn = modal.querySelector('#confirm-delete-btn') as HTMLButtonElement

    let isClosing = false

    const closeModal = () => {
      if (isClosing) return
      isClosing = true
      modal.remove()
    }

    closeBtn?.addEventListener('click', closeModal)
    cancelBtn?.addEventListener('click', closeModal)

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal()
    })

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)

    confirmBtn?.addEventListener('click', async () => {
      if (isClosing || confirmBtn.disabled) return

      const btnText = confirmBtn.querySelector('.btn-text') as HTMLElement
      const btnLoading = confirmBtn.querySelector('.btn-loading') as HTMLElement
      btnText?.classList.add('hidden')
      btnLoading?.classList.remove('hidden')
      confirmBtn.disabled = true

      try {
        await colaboradoresApi.delete(id)
        this.showToast('Colaborador eliminado com sucesso!', 'success')
        closeModal()
        await this.loadColaboradores()
        this.onColaboradorChange?.()
      } catch (error: any) {
        console.error('Failed to delete colaborador:', error)
        let errorMessage = 'Erro ao eliminar colaborador. Tente novamente.'

        if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        this.showToast(errorMessage, 'error')

        btnText?.classList.remove('hidden')
        btnLoading?.classList.add('hidden')
        confirmBtn.disabled = false
      }
    })
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    // Remove existing toasts
    document.querySelectorAll('.toast').forEach(toast => toast.remove())

    const toast = createElement('div', { className: `toast toast-${type}` })
    toast.innerHTML = `
      <div class="toast-content">
        <span class="toast-icon">${type === 'success' ? '✓' : '⚠'}</span>
        <span class="toast-message">${message}</span>
        <button class="toast-close">×</button>
      </div>
    `

    document.body.appendChild(toast)

    setTimeout(() => toast.remove(), 5000)

    const closeBtn = toast.querySelector('.toast-close')
    closeBtn?.addEventListener('click', () => toast.remove())
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  // Public method to refresh data
  async refresh(): Promise<void> {
    await this.loadColaboradores()
  }

  // Public getters
  getCount(): number {
    return this.countInfo.count
  }

  getMaxColaboradores(): number {
    return this.countInfo.max_colaboradores
  }

  canAddMore(): boolean {
    return this.countInfo.can_add_more
  }
}
